import type { LocationGeocodeAddressRefs } from '@app/components/blocks/LocationMapPreview'
import type { InitiativeWithId } from '../../shared/initiativeTypes'

export type ParsedPlotAddress = {
  street: string
  postalCode: string | null
  city: string | null
}

export type InitiativeAddressState = {
  address: string
  resolvedAddress: string
  geoCoords: { lat: number; long: number } | null
  resolvedAddressRefs: LocationGeocodeAddressRefs
}

const normalizePostcode = (value: string): string => {
  const compact = value.replace(/\s/g, '').toUpperCase()
  if (compact.length <= 4) {
    return compact
  }
  return `${compact.slice(0, 4)} ${compact.slice(4, 6)}`
}

const extractPostcode = (value: string): string | null => {
  const match = value.match(/\d{4}\s?[A-Za-z]{2}/)
  return match ? normalizePostcode(match[0]) : null
}

export const parsePlotAddress = (value: string): ParsedPlotAddress => {
  const trimmed = value.trim()
  if (!trimmed) {
    return { street: '', postalCode: null, city: null }
  }

  const postalCode = extractPostcode(trimmed)
  if (!postalCode) {
    return { street: trimmed, postalCode: null, city: null }
  }

  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map((part) => part.trim()).filter(Boolean)
    const postcodeIndex = parts.findIndex(
      (part) => extractPostcode(part) !== null,
    )

    if (postcodeIndex !== -1) {
      const postcodePart = parts[postcodeIndex]
      const inlineCity =
        postcodePart.replace(/\d{4}\s?[A-Za-z]{2}/i, '').trim() || null
      const street = parts.slice(0, postcodeIndex).join(', ').trim()
      const city =
        parts.slice(postcodeIndex + 1).join(', ').trim() || inlineCity || null

      return {
        street: street || trimmed,
        postalCode,
        city,
      }
    }
  }

  const postcodeMatch = trimmed.match(/(\d{4}\s?[A-Za-z]{2})/i)
  if (!postcodeMatch || postcodeMatch.index === undefined) {
    return { street: trimmed, postalCode, city: null }
  }

  const street = trimmed.slice(0, postcodeMatch.index).trim()
  const city = trimmed
    .slice(postcodeMatch.index + postcodeMatch[0].length)
    .trim()

  return {
    street: street || trimmed,
    postalCode,
    city: city || null,
  }
}

const inferPlotAddress = (initiative: InitiativeWithId): string => {
  const storedAddress = initiative.potentialPlotAddress?.trim()
  if (storedAddress) {
    return storedAddress
  }

  const street = initiative.location?.address?.street?.trim()
  const postalCode = initiative.location?.address?.postalCode?.trim()

  if (street && postalCode) {
    return `${street} ${postalCode}`
  }

  return street || ''
}

const emptyResolvedAddressRefs = (): LocationGeocodeAddressRefs => ({})

const readResolvedAddressRefs = (
  initiative: InitiativeWithId,
): LocationGeocodeAddressRefs => {
  const addr = initiative.location?.address
  return {
    country: addr?.country?.id ? { id: addr.country.id } : undefined,
    region: addr?.region?.id ? { id: addr.region.id } : undefined,
    municipality: addr?.municipality?.id
      ? { id: addr.municipality.id }
      : undefined,
    locality: addr?.locality?.id ? { id: addr.locality.id } : undefined,
  }
}

export const createInitiativeAddressState = (
  initiative: InitiativeWithId,
): InitiativeAddressState => {
  const geo = initiative.location?.geo
  return {
    address: inferPlotAddress(initiative),
    resolvedAddress: inferPlotAddress(initiative),
    geoCoords:
      geo?.lat !== undefined && geo?.long !== undefined
        ? { lat: geo.lat, long: geo.long }
        : null,
    resolvedAddressRefs: readResolvedAddressRefs(initiative),
  }
}

export const resetInitiativeAddressState = (): InitiativeAddressState => ({
  address: '',
  resolvedAddress: '',
  geoCoords: null,
  resolvedAddressRefs: emptyResolvedAddressRefs(),
})
